import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, Gift, CheckCircle2, ArrowRight, Printer, Heart } from 'lucide-react';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /redeem and /redeem/:code — public landing page where someone with
 * a partner discount code can claim 1 free month of paid AADC
 * membership.
 */
export default function RedeemPage() {
  const { code: codeFromUrl } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [codeInput, setCodeInput] = useState(codeFromUrl || '');
  const [validation, setValidation] = useState(null);  // { partner_name, free_months } | null
  const [validating, setValidating] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [redeemed, setRedeemed] = useState(null);     // result on success

  // If we landed with a code in the URL, auto-validate it
  useEffect(() => {
    if (codeFromUrl) {
      validateCode(codeFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeFromUrl]);

  const validateCode = async (raw) => {
    const code = (raw || '').trim().toUpperCase();
    if (!code) return;
    setValidating(true);
    setValidation(null);
    try {
      const res = await axios.get(`${API}/partner-codes/lookup/${encodeURIComponent(code)}`);
      setValidation(res.data);
      setCodeInput(code);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Invalid or inactive code';
      toast.error(detail);
      setValidation(null);
    } finally {
      setValidating(false);
    }
  };

  const handleRedeem = async () => {
    if (!validation) return;
    if (!user) {
      // Send to register, preserving the code so they come back here
      navigate(`/register?next=${encodeURIComponent(`/redeem/${validation.code}`)}`);
      return;
    }
    setRedeeming(true);
    try {
      const res = await axios.post(`${API}/partner-codes/redeem/${encodeURIComponent(validation.code)}`);
      setRedeemed(res.data);
      if (res.data.status === 'already_redeemed') {
        toast.info("You've already redeemed this code.");
      } else {
        toast.success(`Redeemed! +${res.data.free_months} free month${res.data.free_months > 1 ? 's' : ''} added.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not redeem');
    } finally {
      setRedeeming(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      <SEO
        title="Redeem your AADC code"
        description="Have a partner code from a rescue or NGO? Redeem it for 1 free month of paid All American Dog Club membership."
        url="/redeem"
      />

      <div className="container mx-auto px-4 py-14 max-w-xl">
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-amber-100 flex items-center justify-center">
            <Gift className="w-8 h-8 text-amber-700" />
          </div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold mb-2">Redeem your AADC code</h1>
          <p className="text-muted-foreground">
            From a rescue, NGO, or breeder? Enter the code they gave you below and we&rsquo;ll add a free month to your membership.
          </p>
        </div>

        {redeemed ? (
          /* SUCCESS STATE */
          <Card className="border-emerald-200" data-testid="redeem-success">
            <CardHeader className="bg-emerald-50 border-b border-emerald-100">
              <CardTitle className="flex items-center gap-2 font-heading">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                {redeemed.status === 'already_redeemed' ? 'Already redeemed' : "You're in!"}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <p>
                {redeemed.status === 'already_redeemed'
                  ? `You've already used the ${redeemed.code} code. Each member can redeem a code once.`
                  : <>You&rsquo;ve been credited <strong>{redeemed.free_months} free month{redeemed.free_months > 1 ? 's' : ''}</strong> of paid AADC membership. Welcome to the Club!</>}
              </p>
              {validation?.partner_name && (
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Heart className="w-4 h-4 text-rose-500" />
                  Thanks to <strong>{validation.partner_name}</strong> for sending you our way.
                </p>
              )}
              <div className="flex gap-2 pt-2">
                <Button onClick={() => navigate('/feed')} className="flex-1" data-testid="redeem-go-feed-btn">
                  Go to the Feed <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button variant="outline" onClick={() => navigate('/profile')} data-testid="redeem-go-profile-btn">
                  My Profile
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* INPUT / VALIDATION STATE */
          <Card data-testid="redeem-input-card">
            <CardContent className="pt-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2" htmlFor="redeem-code-input">
                  Your code
                </label>
                <div className="flex gap-2">
                  <Input
                    id="redeem-code-input"
                    placeholder="AADC-A3F2C9"
                    value={codeInput}
                    onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                    onKeyDown={(e) => { if (e.key === 'Enter') validateCode(codeInput); }}
                    className="font-mono uppercase tracking-wider"
                    data-testid="redeem-code-input"
                    disabled={validating || !!validation}
                  />
                  {!validation && (
                    <Button
                      onClick={() => validateCode(codeInput)}
                      disabled={validating || !codeInput.trim()}
                      data-testid="redeem-check-btn"
                    >
                      {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Check'}
                    </Button>
                  )}
                </div>
              </div>

              {validation && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4" data-testid="redeem-valid-card">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="font-semibold text-emerald-900">Code valid</p>
                      <p className="text-sm text-emerald-800 mt-0.5">
                        {validation.partner_name && (
                          <>
                            From <strong>{validation.partner_name}</strong>.{' '}
                          </>
                        )}
                        Redeem to add <strong>{validation.free_months} free month{validation.free_months > 1 ? 's' : ''}</strong> of paid membership.
                      </p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <Button
                      onClick={handleRedeem}
                      disabled={redeeming}
                      className="w-full"
                      data-testid="redeem-claim-btn"
                    >
                      {redeeming ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Gift className="w-4 h-4 mr-2" />
                      )}
                      {user
                        ? `Add ${validation.free_months} free month${validation.free_months > 1 ? 's' : ''} to my account`
                        : 'Sign up to claim'}
                    </Button>
                    {!user && (
                      <p className="text-xs text-center text-muted-foreground mt-2">
                        Already a member?{' '}
                        <Link
                          to={`/login?next=${encodeURIComponent(`/redeem/${validation.code}`)}`}
                          className="text-amber-700 underline"
                        >
                          Log in to redeem
                        </Link>
                      </p>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Helper for partner agencies */}
        <div className="mt-10 text-center text-sm text-muted-foreground space-y-1">
          <p className="flex items-center justify-center gap-1.5">
            <Printer className="w-4 h-4" />
            Are you a partner agency with a code? You can{' '}
            {validation?.code ? (
              <Link to={`/partner-promo/${validation.code}`} className="text-amber-700 underline">
                print a one-page promo flyer
              </Link>
            ) : (
              <span>get your code from your AADC contact and print a one-page promo at <code className="bg-muted px-1 rounded">/partner-promo/&lt;code&gt;</code></span>
            )}
            .
          </p>
        </div>
      </div>
    </div>
  );
}
